// Billing + payload-hygiene for a care-plan order, shared by the typed form
// and the voice review screen.
//
// Extracted from CarePlanActivityModal so a dictated order is billed on the
// same terms as a typed one. Both now write __billing_charge through the same
// CarePlanServiceAutocomplete, so the only thing that was still forked was the
// dispatch itself.
//
// The strip helpers matter just as much as the poster: __billing_charge and
// __service_meta are internal carriers, and care_plans/ stores whatever data it
// is given verbatim. Leaving them in would persist private UI bookkeeping into
// the clinical record.

import axios from "axios";

// [BILLING] Resolves quantity for billing payload — uses frequency field, else 1.
export const parseBillingQuantity = (entry, activityName) => {
  let frequency = "";
  if (activityName === "medication") {
    frequency = entry?.medication?.frequency ?? entry?.frequency ?? "";
  } else {
    frequency = entry?.frequency ?? "";
  }
  const parsed = Number(String(frequency).trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

// [BILLING] Remarks sent to billing/charges/ — sourced from the notes field.
export const getBillingRemarks = (entry) => String(entry?.notes ?? "").trim();

// [BILLING] Single charge payload for ipd_admission/{id}/billing/charges/ — payout fields vary by patient source.
export const buildSingleBillingChargePayload = ({
  entry,
  activityName,
  isReferredToMePatient,
  referredByProviderId,
}) => {
  const charge = entry.__billing_charge || {};
  const payload = {
    quantity: parseBillingQuantity(entry, activityName),
    remarks: getBillingRemarks(entry) || charge.name || "",
    source_module: "IPD",
  };
  // Prefer medical_service_id (charge-capture/services/) when present; fall back to service_code.
  if (charge.medical_service_id) {
    payload.medical_service_id = charge.medical_service_id;
  } else if (charge.service_code) {
    payload.service_code = charge.service_code;
  }
  if (charge.charge_modifiable === true && charge.rate != null) {
    payload.unit_price = charge.rate;
  }

  if (isReferredToMePatient) {
    if (referredByProviderId) {
      payload.referred_by_provider_id = referredByProviderId;
    }
    payload.payout_eligible = true;
    payload.calculate_doctor_payout = true;
    return payload;
  }

  payload.calculate_doctor_payout = true;
  return payload;
};

// [BILLING] + [LAB_TEST] Removes internal __billing_charge before care_plans/ POST (via onSubmit).
// __billing_charge is read for billing + lab tests in handleSubmit before this strip runs on care plan payload.
export const stripBillingMetaFromEntry = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return entry;
  const { __billing_charge: _billing, ...rest } = entry;
  return rest;
};

// [BILLING] Strips __billing_charge from single entry or multi-entry array payload.
export const stripBillingMetaFromPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload.map((entry) => stripBillingMetaFromEntry(entry));
  }
  return stripBillingMetaFromEntry(payload);
};

// [LAB_TEST] Recursively removes the internal __service_meta helper (added on
// investigation table rows to carry medical_service_id/SNOMED code) so it never
// reaches the care_plans/ payload. Read for lab tests in handleSubmit first.
export const stripServiceMetaDeep = (node) => {
  if (Array.isArray(node)) return node.map(stripServiceMetaDeep);
  if (node && typeof node === "object") {
    const { __service_meta: _serviceMeta, ...rest } = node;
    Object.keys(rest).forEach((key) => {
      rest[key] = stripServiceMetaDeep(rest[key]);
    });
    return rest;
  }
  return node;
};

/**
 * [BILLING] Posts charges after a care plan save.
 * - 1 entry with __billing_charge  → POST ipd_admission/{id}/billing/charges/
 * - 2+ entries with __billing_charge → POST .../billing/charge-capture/bulk-post/
 *
 * Only entries carrying a charge-master selection are billed. That filter is
 * also the safety rail for the voice path: before the review screen had a
 * catalogue picker, a dictated order had no __billing_charge at all and this
 * simply no-opped rather than posting an empty charge.
 */
export const postCarePlanBillingCharges = async ({
  entries,
  admissionId,
  activityName,
  isReferredToMePatient = false,
  referredByProviderId = "",
  headers,
}) => {
  if (!admissionId) return;

  const billableEntries = (Array.isArray(entries) ? entries : [entries]).filter(
    (entry) =>
      entry?.__billing_charge?.medical_service_id ||
      entry?.__billing_charge?.service_code
  );
  if (billableEntries.length === 0) return;

  const baseUrl = `${process.env.REACT_APP_BACKEND_ROUTE_URL}ipd_admission/${admissionId}/billing`;

  // [BILLING] Single charge — payout fields depend on Referred To Me vs admitting list.
  if (billableEntries.length === 1) {
    const entry = billableEntries[0];
    await axios.post(
      `${baseUrl}/charges/`,
      buildSingleBillingChargePayload({
        entry,
        activityName,
        isReferredToMePatient,
        referredByProviderId,
      }),
      { headers }
    );
    return;
  }

  // [BILLING] Multiple charges (Add To Activity queue) — bulk-post; prefer medical_service_id.
  await axios.post(
    `${baseUrl}/charge-capture/bulk-post/`,
    {
      charges: billableEntries.map((entry) => {
        const charge = entry.__billing_charge;
        const row = {
          quantity: parseBillingQuantity(entry, activityName),
        };
        if (charge.medical_service_id) {
          row.medical_service_id = charge.medical_service_id;
        } else if (charge.service_code) {
          row.service_code = charge.service_code;
        }
        return row;
      }),
    },
    { headers }
  );
};
